import React from 'react';

import { Modal } from '@opendoor/bricks/complex';

import {
  estClosingCostDataRow,
  finalizedOfferDataRow,
  FinalOfferContent,
  lateCheckoutDataRow,
  netProceedsDataRow,
  repairCostDataRow,
  serviceChargeDataRow,
  coBrokeCommissionRow,
  agentReferralCommissionRow,
} from 'sellDirect/components/closingDash/finalOffer/FinalOfferContent';
import {
  ClosedOffer,
  FinalizedOffer,
  UnderContractOffer,
  WithdrawnOffer,
} from 'sellDirect/OfferContext';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import {
  useLateCheckout,
  ILateCheckoutTextData,
  NO_LATE_CHECKOUT_COST_TEXT,
} from 'sellDirect/graphql/useLateCheckout';
import { Loader } from '@opendoor/bricks/core';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { useAgentReferralPayments } from 'shared/graphql/useAgentReferralPayments';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';

export interface FinalOfferModalProps {
  offer: FinalizedOffer | UnderContractOffer | WithdrawnOffer | ClosedOffer;
  closingTasks: ClosingTasks;
  isOpen: boolean;
  onRequestClose: () => void;
}

/**
 * Final Offer Content (post-contract-sign) Modal.
 */
export const FinalOfferModal: React.FC<FinalOfferModalProps> = ({
  offer,
  closingTasks,
  isOpen,
  onRequestClose,
}) => {
  const { loading, data } = useLateCheckout(offer, closingTasks.lateCheckout);
  const { sellerInputUUID } = useQualificationContext();
  const {
    eligibleForAgentReferralPayment,
    fetchingAgentReferralPayments,
    agentCommissionCents,
    agentCommissionPercentOfHeadline,
  } = useAgentReferralPayments(sellerInputUUID, offer.shouldSeeAgentExperience);
  if (loading) {
    return <Loader />;
  }

  return (
    <FinalOfferModalWithoutContext
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      offer={offer}
      lateCheckoutTextData={data}
      eligibleForAgentReferralPayment={!!eligibleForAgentReferralPayment}
      agentCommissionCents={agentCommissionCents}
      agentCommissionPercentOfHeadline={agentCommissionPercentOfHeadline}
      fetchingAgentReferralPayments={fetchingAgentReferralPayments}
    />
  );
};

export interface FinalOfferModalWithoutContextProps {
  isOpen: boolean;
  onRequestClose: () => void;
  offer: FinalizedOffer | UnderContractOffer | WithdrawnOffer | ClosedOffer;
  lateCheckoutTextData: ILateCheckoutTextData;
  eligibleForAgentReferralPayment: boolean;
  agentCommissionCents: number;
  agentCommissionPercentOfHeadline: number;
  fetchingAgentReferralPayments: boolean;
}

// component-test-check-ignore
/**
 * Final Offer Content post contract sign (modal) without context.
 */
export const FinalOfferModalWithoutContext: React.FC<FinalOfferModalWithoutContextProps> = ({
  isOpen,
  onRequestClose,
  offer,
  lateCheckoutTextData,
  eligibleForAgentReferralPayment,
  agentCommissionCents,
  agentCommissionPercentOfHeadline,
  fetchingAgentReferralPayments,
}) => {
  const {
    assertions: { isLWCBuyerFallThroughProtectionEnabled },
  } = useExperimentationContext();
  const { showSixPercentFee } = useQualificationContext();
  const showNewFee = isLWCBuyerFallThroughProtectionEnabled && showSixPercentFee;
  const rowData = [
    finalizedOfferDataRow(offer.underwriting),
    serviceChargeDataRow(offer.underwriting, showNewFee),
    estClosingCostDataRow(offer.underwriting),
    repairCostDataRow(offer.shouldSeeAgentExperience, offer.underwriting),
  ];

  if (lateCheckoutTextData.totalCostDollarsText !== NO_LATE_CHECKOUT_COST_TEXT) {
    // if doing late checkout, add that to the data row
    rowData.push(lateCheckoutDataRow(offer.shouldSeeAgentExperience, lateCheckoutTextData));
  }

  // if agent experience & listed, show co-broke commission
  if (offer.shouldSeeAgentExperience && offer.lead.isListed) {
    rowData.push(coBrokeCommissionRow(offer.coBrokePercentage, offer.underwriting));
  }

  rowData.push(netProceedsDataRow(offer.shouldSeeAgentExperience, offer.underwriting));

  // if eligible, show agent referral commission
  if (offer.shouldSeeAgentExperience && agentCommissionPercentOfHeadline != 0) {
    rowData.push(
      agentReferralCommissionRow(
        eligibleForAgentReferralPayment,
        agentCommissionCents,
        agentCommissionPercentOfHeadline,
      ),
    );
  }

  return (
    <Modal
      analyticsName="cake-final-offer-modal"
      isOpen={isOpen}
      shouldCloseOnOverlayClick
      onRequestClose={onRequestClose}
      headerProps={{ title: 'Finalized offer price', titleProps: { as: 'h2' } }}
    >
      {fetchingAgentReferralPayments ? (
        <Loader />
      ) : (
        <FinalOfferContent rowData={rowData} inModal={true} />
      )}
    </Modal>
  );
};
